import axios from "axios";

const CACHE_KEY = "coingecko_simple_prices_v1";
const TTL_MS = 5 * 60 * 1000; // 5 minutes
const PRICE_URL =
  "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin%2Cethereum%2Cbinancecoin%2Ctron%2Ctether&vs_currencies=usd&include_24hr_change=true";

let memoryCache = null;
let inflight = null;

const readStorage = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeStorage = (entry) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // ignore quota / private-mode failures
  }
};

const isFresh = (entry) =>
  Boolean(entry?.data && Date.now() - entry.fetchedAt < TTL_MS);

/** Latest cached CoinGecko payload, or null */
export const getCachedPrices = () => {
  if (memoryCache?.data) return memoryCache.data;
  const stored = readStorage();
  if (stored?.data) {
    memoryCache = stored;
    return stored.data;
  }
  return null;
};

/**
 * Fetch CoinGecko simple prices with TTL cache + in-flight dedupe.
 * On 429 (or other fetch failures), returns cached prices when available.
 */
export const fetchCryptoPrices = async ({ force = false } = {}) => {
  const existing = memoryCache || readStorage();
  if (!force && isFresh(existing)) {
    memoryCache = existing;
    return existing.data;
  }

  if (inflight) return inflight;

  inflight = axios
    .get(PRICE_URL)
    .then((res) => {
      const entry = { data: res.data, fetchedAt: Date.now() };
      memoryCache = entry;
      writeStorage(entry);
      return entry.data;
    })
    .catch((error) => {
      const status = error?.response?.status;
      const fallback = getCachedPrices();
      if (fallback && (status === 429 || !error.response || status >= 500)) {
        console.warn(
          "CoinGecko unavailable; using cached prices",
          status || error.message
        );
        return fallback;
      }
      throw error;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
};
